/*
  Timetable: a week strip (Monday first, today ringed in the portal's teal) and the day's lessons
  as rows. The portal shows a react-big-calendar week on desktop and a day view on phones; this is
  that day view, built for thumbs, with the same data and colours.
*/
import { LeftOutlined, RightOutlined, ReloadOutlined } from "@ant-design/icons";
import Avatar from "app/components/Icons/Avatar";
import LessonRow from "app/components/LessonRow";
import LessonSheet, { studentNamesFor } from "app/components/LessonSheet";
import { TopBar } from "app/components/Shell";
import Spinner, { EmptyState } from "app/components/Spinner";
import { Button } from "app/components/v1/Button";
import { useGetTimetableQuery } from "app/services/timetable";
import type { Lesson } from "app/services/types";
import { MAIN_SELECTORS } from "app/store";
import { isoDay } from "app/utils/time";
import moment from "moment";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

export default function Timetable() {
  const prefs = useSelector(MAIN_SELECTORS.reminders);
  const { data, isLoading, isFetching, refetch } = useGetTimetableQuery({ daysBack: 14, daysAhead: 28 });
  const [weekStart, setWeekStart] = useState(() => moment().startOf("week"));
  const [selected, setSelected] = useState(() => moment().format("YYYY-MM-DD"));
  const [open, setOpen] = useState<Lesson | null>(null);
  const [studentFilter, setStudentFilter] = useState<number | null>(null);

  const children = data?.children ?? [];
  const byDay = useMemo(() => {
    const m = new Map<string, Lesson[]>();
    for (const l of data?.lessons ?? []) {
      if (studentFilter && !l.students.some((s) => s.id === studentFilter && !s.onLeave)) continue;
      const k = isoDay(l.start); m.set(k, [...(m.get(k) ?? []), l]);
    }
    return m;
  }, [data, studentFilter]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, "days")), [weekStart]);
  const todayKey = moment().format("YYYY-MM-DD");
  const dayLessons = byDay.get(selected) ?? [];
  const inRange = data ? selected >= data.from && selected <= data.to : true;

  return (
    <div className="ct-page">
      <TopBar title="Timetable" right={<Button buttonType="text" color="secondary" size="sm" aria-label="Refresh" icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()} />} />

      {children.length > 1 && (
        <div className="d-flex gap-2 flex-wrap mb-2">
          <button type="button" className={`btn btn-sm rounded-pill ${studentFilter === null ? "btn-primary" : "btn-light"}`} onClick={() => setStudentFilter(null)}>All</button>
          {children.map((c) => (
            <button key={c.id} type="button" className={`btn btn-sm rounded-pill d-inline-flex align-items-center gap-1 ${studentFilter === c.id ? "btn-primary" : "btn-light"}`} onClick={() => setStudentFilter(c.id)}>
              <Avatar name={c.avatar} fullName={`${c.name} ${c.lastName}`} size={20} /> {c.name}
            </button>
          ))}
        </div>
      )}

      <div className="d-flex align-items-center justify-content-between mt-2">
        <Button buttonType="text" color="secondary" size="sm" aria-label="Previous week" icon={<LeftOutlined />} onClick={() => setWeekStart(weekStart.clone().subtract(1, "week"))} />
        <div style={{ fontWeight: 700, color: "#171725" }}>{weekStart.format("D MMM")} - {weekStart.clone().add(6, "days").format("D MMM YYYY")}</div>
        <Button buttonType="text" color="secondary" size="sm" aria-label="Next week" icon={<RightOutlined />} onClick={() => setWeekStart(weekStart.clone().add(1, "week"))} />
      </div>
      <div className="ct-week" role="tablist" aria-label="Days of the week">
        {days.map((d) => {
          const key = d.format("YYYY-MM-DD");
          return (
            <button key={key} type="button" role="tab" aria-selected={key === selected} className={`${key === todayKey ? "today" : ""} ${key === selected ? "selected" : ""} ${byDay.has(key) ? "has" : ""}`} onClick={() => setSelected(key)}>
              <span>{d.format("dd")}</span>
              <span className="d">{d.format("D")}</span>
              <span className="m" />
            </button>
          );
        })}
      </div>
      {weekStart.format("YYYY-MM-DD") !== moment().startOf("week").format("YYYY-MM-DD") && (
        <div className="text-center mb-2"><Button buttonType="text" size="sm" name="Back to this week" onClick={() => { setWeekStart(moment().startOf("week")); setSelected(todayKey); }} /></div>
      )}

      <h2 className="ct-section-title" style={{ fontSize: 16 }}>{selected === todayKey ? "Today" : moment(selected).format("dddd D MMMM")}</h2>
      {isLoading && !data ? <Spinner tip="Loading the timetable" /> : !inRange ? (
        <EmptyState title="Not on the timetable yet" hint="Lessons are added up to six weeks ahead." />
      ) : dayLessons.length ? (
        dayLessons.map((l) => <LessonRow key={l.registerId} lesson={l} studentNames={studentNamesFor(l, children)} onClick={() => setOpen(l)} />)
      ) : (
        <EmptyState title="No lessons this day" />
      )}

      <LessonSheet lesson={open} children={children} prefs={prefs} onClose={() => setOpen(null)} />
    </div>
  );
}
